/**
 * Shared canvas drawing primitives — heart, petal, kiss, diamond,
 * polaroid, smooth path, rounded rect. Imported by every per-effect
 * file in `effects/`.
 */

import type { Fragment, Particle } from './types';
import { rgba } from './utils';

export function drawHeart(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
  alpha: number,
  rotation: number,
  fill: boolean,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.scale(size / 32, size / 32);
  ctx.beginPath();
  ctx.moveTo(0, 10);
  ctx.bezierCurveTo(-22, -8, -10, -24, 0, -12);
  ctx.bezierCurveTo(10, -24, 22, -8, 0, 10);
  ctx.closePath();
  ctx.globalAlpha = Math.max(0, alpha);
  if (fill) {
    ctx.fillStyle = color;
    ctx.fill();
  } else {
    ctx.strokeStyle = color;
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

export function drawPetal(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
  alpha: number,
  rotation: number,
  wave: number,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.scale(size / 16, size / 16);
  const curl = Math.sin(wave) * 2;
  ctx.globalAlpha = Math.max(0, alpha);
  const gradient = ctx.createLinearGradient(-8, -12, 8, 12);
  gradient.addColorStop(0, rgba(color, 0.75));
  gradient.addColorStop(0.52, rgba('#FADADD', 0.55));
  gradient.addColorStop(1, rgba(color, 0.9));
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.moveTo(0, -12);
  ctx.bezierCurveTo(12 + curl, -6, 9, 10, 0, 14);
  ctx.bezierCurveTo(-9, 10, -12 - curl, -6, 0, -12);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba('#FFFFFF', 0.1);
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.moveTo(0, -9);
  ctx.quadraticCurveTo(curl, 1, 0, 10);
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.restore();
}

export function drawKiss(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
  alpha: number,
  rotation: number,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.scale(size / 44, size / 44);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(-9, -4, 13, 7, -0.18, 0, Math.PI * 2);
  ctx.ellipse(9, -4, 13, 7, 0.18, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-20, 2);
  ctx.bezierCurveTo(-8, 13, 9, 13, 20, 2);
  ctx.bezierCurveTo(9, 8, -8, 8, -20, 2);
  ctx.closePath();
  ctx.fill();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.globalAlpha = alpha * 0.45;
  ctx.beginPath();
  ctx.moveTo(-17, 0);
  ctx.quadraticCurveTo(0, 4, 17, 0);
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 2.5;
  ctx.stroke();
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.restore();
}

export function drawDiamond(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
  alpha: number,
  rotation: number,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -size);
  ctx.lineTo(size * 0.8, 0);
  ctx.lineTo(0, size);
  ctx.lineTo(-size * 0.8, 0);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.restore();
}

export function drawSmoothPath(
  ctx: CanvasRenderingContext2D,
  points: Particle[],
): void {
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length - 1; i += 1) {
    const current = points[i];
    const next = points[i + 1];
    ctx.quadraticCurveTo(
      current.x,
      current.y,
      (current.x + next.x) / 2,
      (current.y + next.y) / 2,
    );
  }
  const last = points[points.length - 1];
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
}

export function drawPolaroid(
  ctx: CanvasRenderingContext2D,
  fragment: Fragment,
  time: number,
): void {
  ctx.save();
  ctx.translate(fragment.x, fragment.y);
  ctx.rotate(fragment.rot);
  ctx.fillStyle = '#F4E6CF';
  roundRect(ctx, -fragment.w / 2, -fragment.h / 2, fragment.w, fragment.h, 4);
  ctx.fill();

  const inset = fragment.w * 0.08;
  const photoH = fragment.h * 0.72;
  const photoGradient = ctx.createLinearGradient(
    -fragment.w / 2,
    -fragment.h / 2,
    fragment.w / 2,
    fragment.h / 2,
  );
  photoGradient.addColorStop(0, fragment.color);
  photoGradient.addColorStop(0.55, rgba('#1F2327', 0.9));
  photoGradient.addColorStop(1, rgba('#FADADD', 0.65));
  ctx.fillStyle = photoGradient;
  roundRect(
    ctx,
    -fragment.w / 2 + inset,
    -fragment.h / 2 + inset,
    fragment.w - inset * 2,
    photoH,
    3,
  );
  ctx.fill();

  ctx.strokeStyle = rgba('#FFFFFF', 0.32);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(
    -fragment.w * 0.32,
    -fragment.h * 0.06 + Math.sin(time + fragment.seed) * 2,
  );
  ctx.quadraticCurveTo(
    -fragment.w * 0.03,
    -fragment.h * 0.25,
    fragment.w * 0.32,
    -fragment.h * 0.04,
  );
  ctx.stroke();
  ctx.fillStyle = rgba('#F4E6CF', 0.7);
  ctx.fillRect(-fragment.w * 0.3, fragment.h * 0.34, fragment.w * 0.42, 2);
  ctx.fillRect(-fragment.w * 0.3, fragment.h * 0.4, fragment.w * 0.25, 2);
  ctx.restore();
}

export function paintBackground(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  top: string,
  middle: string,
  bottom: string,
): void {
  const gradient = ctx.createLinearGradient(0, 0, 0, h);
  gradient.addColorStop(0, top);
  gradient.addColorStop(0.52, middle);
  gradient.addColorStop(1, bottom);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);
}

export function paintVignette(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  strength: number,
): void {
  const gradient = ctx.createRadialGradient(
    w * 0.5,
    h * 0.42,
    w * 0.12,
    w * 0.5,
    h * 0.48,
    h * 0.72,
  );
  gradient.addColorStop(0, 'rgba(0,0,0,0)');
  gradient.addColorStop(0.58, `rgba(0,0,0,${strength * 0.18})`);
  gradient.addColorStop(1, `rgba(0,0,0,${strength})`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);
}

export function addSoftSpot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: string,
): void {
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}
